import { useMemo } from 'react'
import type { MenuProps } from 'antd'

interface LevelKeysProps {
  key?: string
  children?: LevelKeysProps[]
}

/**
 * 获取菜单项的级别映射
 */
const getLevelKeys = (items: LevelKeysProps[]) => {
  const key: Record<string, number> = {}
  
  const func = (itemsList: LevelKeysProps[], level = 1) => {
    itemsList.forEach((item) => {
      if (item.key) {
        key[item.key] = level
      }
      if (item.children) {
        func(item.children, level + 1)
      }
    })
  }
  
  func(items)
  return key
}

/**
 * 菜单 Accordion 模式的 Hook
 * 实现只展开当前父级菜单的逻辑
 */
export const useMenuAccordion = (menuItems: MenuProps['items']) => {
  const levelKeys = useMemo(() => {
    return getLevelKeys(menuItems as LevelKeysProps[])
  }, [menuItems])

  const createOnOpenChange = (currentOpenKeys: string[]) => (openKeys: string[]) => {
    const currentOpenKey = openKeys.find((key) => !currentOpenKeys.includes(key))
    
    // 展开操作
    if (currentOpenKey !== undefined) {
      const currentLevel = levelKeys[currentOpenKey]
      
      if (currentLevel !== undefined) {
        // 查找与当前菜单同级别的其他菜单的索引
        const repeatIndex = openKeys.findIndex(
          (key) => key !== currentOpenKey && levelKeys[key] === currentLevel
        )

        // 移除同级别的其他菜单（如果找到了）
        const withoutRepeat = repeatIndex !== -1 
          ? openKeys.filter((_, index) => index !== repeatIndex)
          : openKeys

        // 移除比当前级别低的所有子菜单，只保留当前级别及以上的
        const result = withoutRepeat.filter((key) => {
          const keyLevel = levelKeys[key]
          return keyLevel !== undefined && keyLevel <= currentLevel
        })
        
        return result
      }
      
      return openKeys
    }
    
    // 收起操作
    return openKeys
  }

  return createOnOpenChange
}

export default useMenuAccordion

